import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Handbook } from '~/types';
import { getHandbookBySolution } from '~/api/handbook';
import { useCurrentBusiness } from './businesses';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const useHandbook = () => {
  return useQuery<Handbook>('handbooks', () => getHandbookBySolution().then((res) => res.data), {
    staleTime: Infinity,
  });
};

type BusinessLegalTypes = NonNullable<Required<Handbook>['countries'][0]['businessLegalTypes']>;

type HandbookDataSelector<D> = {
  data: D;
  isLoading: boolean;
  options: { label: string; value: string }[];
};

export type UseLegalType = HandbookDataSelector<BusinessLegalTypes>;

export const useLegalType = (countryId: string | undefined): UseLegalType => {
  const { data, isLoading } = useHandbook();
  const country = data?.countries?.find((item) => item.id === countryId);
  const businessLegalTypes = country?.businessLegalTypes || [];

  return {
    data: businessLegalTypes,
    isLoading,
    options: businessLegalTypes.map((legalType) => ({
      label: legalType.name,
      value: legalType.name,
    })),
  };
};

type BusinessDocumentType = NonNullable<BusinessLegalTypes[0]['documents']>;

export type UseBusinessDocumentType = HandbookDataSelector<BusinessDocumentType>;

export const useBusinessDocumentType = (props: {
  countryId: string | undefined;
  legalType: string | undefined;
  filterOptions?: (value: BusinessDocumentType[0]) => boolean;
}): UseBusinessDocumentType => {
  const { countryId, legalType, filterOptions = () => true } = props;

  const { data, isLoading } = useLegalType(countryId);
  const businessDocumentType =
    data.find((legalTypeData) => legalTypeData.name === legalType)?.documents || [];

  return {
    data: businessDocumentType,
    isLoading,
    options: businessDocumentType.filter(filterOptions).map((documentType) => ({
      label: documentType.type,
      value: documentType.type,
    })),
  };
};

type BusinessDocumentTypeParameters = NonNullable<BusinessDocumentType[0]['parameters']>;

export type UseBusinessDocumentTypeParameters =
  HandbookDataSelector<BusinessDocumentTypeParameters>;

export const useBusinessDocumentTypeParameters = (props: {
  countryId: string | undefined;
  legalType: string | undefined;
  documentType: string | undefined;
  filterOptions?: (value: BusinessDocumentTypeParameters[0]) => boolean;
}): UseBusinessDocumentTypeParameters => {
  const { countryId, legalType, documentType, filterOptions = () => true } = props;

  const [t] = useTranslation('documentTypeParameters');

  const { data, isLoading } = useBusinessDocumentType({ countryId, legalType });

  const parameters =
    data.find((documentTypeData) => documentTypeData.type === documentType)?.parameters || [];

  return {
    data: parameters,
    isLoading,
    options: parameters.filter(filterOptions).map((param) => ({
      label: t(param.name),
      value: param.name,
    })),
  };
};

type BusinessDocumentTypePageNames = NonNullable<BusinessDocumentType[0]['pageNames']>;

export type UseBusinessDocumentTypePageNames = HandbookDataSelector<BusinessDocumentTypePageNames>;

export const useBusinessDocumentTypePageNames = (props: {
  countryId: string | undefined;
  legalType: string | undefined;
  documentType: string | undefined;
}): UseBusinessDocumentTypePageNames => {
  const { countryId, legalType, documentType } = props;

  const [t] = useTranslation('documentTypePageNames');

  const { data, isLoading } = useBusinessDocumentType({ countryId, legalType });

  const pageNames =
    data.find((documentTypeData) => documentTypeData.type === documentType)?.pageNames || [];

  return {
    data: pageNames,
    isLoading,
    options: pageNames.map((name) => ({
      label: t(name),
      value: name,
    })),
  };
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useFirstDayOfWeek = () => {
  const hb = useHandbook();
  const business = useCurrentBusiness();
  const firstWeekDay =
    hb.data?.countries.find((item) => item.id === business?.data?.country)?.firstDayOfWeek || 'MO';
  return { ...hb, data: firstWeekDay };
};

export default useHandbook;
